import * as React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Action, ActionType, Modal} from '>shared/components/modal/modal';
import {useModal} from '>shared/components/modal/useModal';
import {Permissions, UserRole} from '>generated/dvp.types';
import {useAsyncDispatch} from '>root/store/main';
import {addUser} from '>root/store/actions/account';
import {Input} from '>shared/components/form/input';
import {
  FormBuilder,
  FormBuilderFields,
  FormBuilderFieldType,
  useAsyncSubmit,
} from '>shared/components/form/formBuilder';
import {useSnackbar} from '>shared/components/snackbars/useSnackbar';
import {CheckBox} from '>shared/components/form/checkBox';
import {ControlledSelect} from '>shared/components/form/select';
import {Option} from '>shared/components/form/rawControls/rawSelect';
import {Paragraph} from '>shared/components/typography/paragraph';
import styled from '@emotion/styled';
import {vr5} from '>shared/styles/mixins/verticalRhythm';
import {baseFontSize6, baseFontSize7} from '>shared/components/typography/baseTypography';
import {ModalDisplayType} from '>shared/components/modal/displayType';
import {getCustomerMessage} from '>lib/util';

interface AddUserModalProps {
  accountId: string;
  roleOptions: Option[];
}

const RoleDescriptionHeader = styled.div`
  margin-top: 2rem;
`;

const RoleDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const RoleName = styled(Paragraph)`
  min-width: 18rem;
  ${baseFontSize7};
  font-weight: bold;
`;

const RoleDescription = styled.span`
  ${baseFontSize7};
`;

interface NewUserInput {
  email: string;
  role: UserRole;
  importAccess: boolean;
}

function canRoleHavePermissionsChanged(role: string): boolean {
  return role !== UserRole.Coach;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  accountId,
  roleOptions,
}: AddUserModalProps) => {
  const {hideModal} = useModal();
  const [isSaving, setIsSaving] = React.useState(false);
  const asyncDispatch = useAsyncDispatch();
  const {showSuccessAlert} = useSnackbar();

  const formMethods = useForm<NewUserInput>({
    mode: 'all',
    defaultValues: {
      importAccess: false,
    },
  });

  const {register} = formMethods;

  const handleInvite = React.useCallback(
    (updatedCanManageBilling: boolean) => async (
      newUserInput: NewUserInput,
      event: React.BaseSyntheticEvent | undefined,
      setFormError: (message: string) => void
    ) => {
      event?.preventDefault();
      setIsSaving(true);
      let hasError = false;
      const newUser = newUserInput;

      const permissions: Permissions = {
        canBulkUploadEmployees: newUser.role === UserRole.AccountAdmin,
        canManageBilling: updatedCanManageBilling,
      };

      try {
        await asyncDispatch(
          addUser({accountId, email: newUser.email, role: newUser.role, permissions})
        );
      } catch (err) {
        const error = err as Error;
        hasError = true;

        if (error.message) {
          setFormError(getCustomerMessage(error.message));
        } else {
          throw err;
        }
      }

      setIsSaving(false);

      if (!hasError) {
        showSuccessAlert('New User Added');
        // redirect to user details page in story: SW-11958
        hideModal();
      }
    },
    []
  );

  const [canManageBilling, setCanManageBilling] = React.useState(false);
  const [role, setRole] = React.useState<string>(UserRole.AccountAdmin);

  const onRoleChange = React.useCallback((newRole: string) => {
    setRole(newRole);
    if (newRole === UserRole.Coach) {
      setCanManageBilling(false);
    }
  }, []);

  const submitFormAndClose = useAsyncSubmit(formMethods, handleInvite(canManageBilling), [
    handleInvite,
    canManageBilling,
  ]);

  const modalActions: Action[] = [
    {
      type: ActionType.Primary,
      label: 'Invite',
      onClick: submitFormAndClose,
      inputType: 'submit',
      disabled: isSaving,
      qaDataAttribute: 'invite-button',
    },
    {
      type: ActionType.Secondary,
      label: 'Cancel',
      onClick: hideModal,
      qaDataAttribute: 'cancel-invite-button',
    },
  ];

  const userFormFields: FormBuilderFields = [
    {
      type: FormBuilderFieldType.Input,
      label: 'Email',
      name: 'email',
      component: (
        <Input
          autoComplete="email"
          ref={register({
            required: 'Email is required',
            pattern: {
              value: /^(?![_.-])((?![_.-][_.-])[a-zA-Z\d\+_.-]){0,63}[a-zA-Z\d]@((?!-)((?!--)[a-zA-Z\d-]){0,63}[a-zA-Z\d]\.){1,2}([a-zA-Z]{2,14}\.)?[a-zA-Z]{2,14}$/,
              message: 'Email is not valid',
            },
            validate: {
              required: (value: string) => {
                if (value.trim().length === 0) {
                  return 'Email is required';
                }
              },
            },
          })}
          placeholder="Email Address"
          required
        />
      ),
    },
    {
      type: FormBuilderFieldType.Input,
      label: 'Role',
      name: 'role',
      component: (
        <ControlledSelect
          rules={{required: 'Role is required'}}
          options={roleOptions}
          onSelectChange={onRoleChange}
          isSearchable={false}
          required
          placeholder=""
        />
      ),
    },
  ];

  const userPermissionFormFields: FormBuilderFields = [
    {
      type: FormBuilderFieldType.Custom,
      name: 'billingAccess',
      component: (
        <CheckBox
          label="Allow billing access"
          checked={canManageBilling}
          onChange={() => setCanManageBilling(!canManageBilling)}
        />
      ),
    },
  ];

  const formBuilderFields: FormBuilderFields = [
    {
      type: FormBuilderFieldType.Group,
      fields: userFormFields,
    },
  ];

  if (canRoleHavePermissionsChanged(role)) {
    formBuilderFields.push({
      type: FormBuilderFieldType.Group,
      fields: userPermissionFormFields,
      label: 'Permission Settings',
    });
  }

  return (
    <FormProvider {...formMethods}>
      <Modal
        headerOptions={{title: 'Add User'}}
        displayType={ModalDisplayType.Small}
        actions={modalActions}
        isLocked={isSaving}
      >
        <div>
          <FormBuilder css={vr5} fields={formBuilderFields} />
        </div>
        <RoleDescriptionHeader css={[baseFontSize6]}>Role Descriptions</RoleDescriptionHeader>
        <RoleDetailContainer>
          <RoleName>Account Admin</RoleName>
          <RoleDescription>Access to all employee data</RoleDescription>
        </RoleDetailContainer>
        <RoleDetailContainer>
          <RoleName>Coach</RoleName>
          <RoleDescription>Access to shared results only</RoleDescription>
        </RoleDetailContainer>
      </Modal>
    </FormProvider>
  );
};
